import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { TabPanel, TabView } from 'primereact/tabview';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { DeleteCoachingDialog } from '../../../components/deleteCoaching/DeleteCoachingDialog';
import { selectCurrentEmpid } from '../../../pages/login/authSlice';
import { useDeleteCoachingMutation } from '../coachingSlice';
import { RevgenGoalReview } from './RevgenGoalReview';
import { RevgenGoalSetting } from './RevgenGoalSetting';
import './revgen.scss';

interface RevGenGoalsTableProps {
    goals: any;
    recipientDetails: any;
    hideControls: boolean;
    isSupervisorReviewed: boolean;
    isAssociateAcknowledged: boolean;
    inputMonth: number;
    inputYear: number;
    viewState?: string;
}

export const RevGenGoalsTable = ({
    recipientDetails,
    goals,
    hideControls,
    isSupervisorReviewed,
    isAssociateAcknowledged,
    inputMonth,
    inputYear,
    viewState,
}: RevGenGoalsTableProps) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [expandedRows, setExpandedRows] = useState<any>([]);
    const [diaDelete, setDiaDelete] = useState<boolean>(false);
    const [DeleteCoaching] = useDeleteCoachingMutation();
    const currEmpId = useSelector(selectCurrentEmpid);
    const [goalId, setGoalId] = useState(0);

    const rowExpansionTemplate = (rowData: any) => {
        return (
            <TabView activeIndex={activeIndex} onChange={(e: any) => setActiveIndex(e.index)}>
                <TabPanel header="Goal Setting">
                    <RevgenGoalSetting
                        data={rowData}
                        recipientDetails={recipientDetails}
                        setExpandedRows={setExpandedRows}
                        hideControls={hideControls}
                        isSupervisorReviewed={isSupervisorReviewed}
                        isAssociateAcknowledged={isAssociateAcknowledged}
                        inputMonth={inputMonth}
                        inputYear={inputYear}
                        viewState={viewState}
                    />
                </TabPanel>
                <TabPanel
                    header="Month-End Review"
                    disabled={recipientDetails?.associate_acknowledged === false}
                >
                    <RevgenGoalReview
                        data={rowData}
                        recipientDetails={recipientDetails}
                        setExpandedRows={setExpandedRows}
                        hideControls={hideControls}
                    />
                </TabPanel>
            </TabView>
        );
    };
    const renderTarget = (rowData: any) => {
        return rowData?.target_goal_value || '--';
    };
    const renderActual = (rowData: any) => {
        return rowData?.actual_goal_value || '--';
    };

    const handleDelete = () => {
        const payload = {
            coachingId: goalId,
            coachingItem: 'revgen',
        };
        DeleteCoaching(payload)
            .unwrap()
            .then((res: any) => {
                if (res?.data) {
                    toast.success('Goal Deleted successfully');
                    setDiaDelete(false);
                } else {
                    toast.error('Error Deleting Goal');
                }
            })
            .catch(() => {
                toast.error('Error Deleting Goal');
            });
    };

    const deleteIconClicked = (rowData: any) => {
        if (isSupervisorReviewed || isAssociateAcknowledged) {
            toast.warning('Cannot Delete Reviewed Goal.');
        }
        else if (rowData?.author_employee_number === currEmpId) {
            setDiaDelete(true);
            setGoalId(rowData?.coaching_goal_id);
        } else toast.warning('Only author can delete.');
    };

    const renderDelete = (rowData: any) => {
        return (
            <Button
                className="deleteBtnIcon p-button-outlined border-0 float-end me-3"
                onClick={() => deleteIconClicked(rowData)}
            />
        );
    };

    const allowExpansion = (rowData: any) => {
        if (rowData) return true;

        return false;
    };

    return (
        <>
            <h4 className="mt-2">Associate Goals</h4>
            <div className="card-body p-0 align-middle text-center table">
                <DataTable
                    value={goals[0]?.coachingGoals}
                    expandedRows={expandedRows}
                    onRowToggle={(e) => {
                        setExpandedRows(e.data);
                    }}
                    rowExpansionTemplate={rowExpansionTemplate}
                    dataKey="coaching_goal_id"
                    selectionMode="single"
                    className="goals-table"
                    rows={5}
                    paginator={goals?.length > 5}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                >
                    <Column field="goal_title" header="Title" />
                    <Column field="target_goal_value" header="Target" body={renderTarget} />
                    <Column field="actual_goal_value" header="Actual" body={renderActual} />
                    <Column body={renderDelete} />
                    <Column key="expand" expander={allowExpansion} />
                </DataTable>
            </div>
            {diaDelete && (
                <DeleteCoachingDialog
                    diaDelete={diaDelete}
                    setDiaDelete={setDiaDelete}
                    handleDelete={handleDelete}
                />
            )}
        </>
    );
};
